import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/App';
import apiClient from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const StudentList = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // New student form
  const [newStudent, setNewStudent] = useState({
    full_name: '',
    email: '',
    phone: '',
    whatsapp_number: '',
    lead_source: 'website',
    desired_country: '',
    desired_course: 'MBBS',
    notes: ''
  });

  useEffect(() => {
    fetchStudents();
  }, [statusFilter, stageFilter]);

  const fetchStudents = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('lead_status', statusFilter);
      if (stageFilter && stageFilter !== 'all') params.append('admission_stage', stageFilter);
      
      const response = await apiClient.get(`/students?${params.toString()}`);
      setStudents(response.data);
    } catch (error) {
      toast.error('Error fetching students');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await apiClient.post('/students', newStudent);
      setStudents([response.data, ...students]);
      setShowAddDialog(false);
      setNewStudent({
        full_name: '',
        email: '',
        phone: '',
        whatsapp_number: '',
        lead_source: 'website',
        desired_country: '',
        desired_course: 'MBBS',
        notes: ''
      });
      toast.success('Student added successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error adding student';
      toast.error('Failed to add student', {
        description: message
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredStudents = students.filter(student => {
    const searchLower = searchTerm.toLowerCase();
    return (
      student.full_name.toLowerCase().includes(searchLower) ||
      student.email.toLowerCase().includes(searchLower) ||
      student.phone.includes(searchTerm)
    );
  });

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'hot': 'bg-red-100 text-red-800 hover:bg-red-200',
      'warm': 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
      'cold': 'bg-blue-100 text-blue-800 hover:bg-blue-200'
    };
    return statusMap[status] || 'bg-gray-100 text-gray-800';
  };

  const getStageBadgeClass = (stage) => {
    const stageMap = {
      'inquiry': 'bg-gray-100 text-gray-800',
      'application': 'bg-blue-100 text-blue-800',
      'document_submission': 'bg-yellow-100 text-yellow-800',
      'offer_letter': 'bg-green-100 text-green-800',
      'visa': 'bg-purple-100 text-purple-800',
      'departure': 'bg-emerald-100 text-emerald-800'
    };
    return stageMap[stage] || 'bg-gray-100 text-gray-800';
  };

  const formatStageText = (stage) => {
    if (!stage) return 'N/A';
    return stage.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatStatusText = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-24 bg-gray-200 rounded-lg animate-pulse"></div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Prospects</h1>
          <p className="text-gray-600">Students in touch but not confirmed (no registration fee paid)</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-student-button">
              <i className="fas fa-plus mr-2"></i>
              Add Prospect
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add New Student</DialogTitle>
              <DialogDescription>
                Add a new student inquiry to the system.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddStudent} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    value={newStudent.full_name}
                    onChange={(e) => setNewStudent({...newStudent, full_name: e.target.value})}
                    required
                    data-testid="add-student-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newStudent.email}
                    onChange={(e) => setNewStudent({...newStudent, email: e.target.value})}
                    required
                    data-testid="add-student-email-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    value={newStudent.phone}
                    onChange={(e) => setNewStudent({...newStudent, phone: e.target.value})}
                    required
                    data-testid="add-student-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="whatsapp_number">WhatsApp Number</Label>
                  <Input
                    id="whatsapp_number"
                    value={newStudent.whatsapp_number}
                    onChange={(e) => setNewStudent({...newStudent, whatsapp_number: e.target.value})}
                    data-testid="add-student-whatsapp-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lead_source">Lead Source</Label>
                  <Select value={newStudent.lead_source} onValueChange={(value) => setNewStudent({...newStudent, lead_source: value})}>
                    <SelectTrigger data-testid="add-student-source-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="meta_ads">Meta Ads</SelectItem>
                      <SelectItem value="instagram">Instagram</SelectItem>
                      <SelectItem value="youtube">YouTube</SelectItem>
                      <SelectItem value="website">Website</SelectItem>
                      <SelectItem value="walk_in">Walk-in</SelectItem>
                      <SelectItem value="partner_office">Partner Office</SelectItem>
                      <SelectItem value="referral">Referral</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="desired_country">Desired Country</Label>
                  <Input
                    id="desired_country"
                    value={newStudent.desired_country}
                    onChange={(e) => setNewStudent({...newStudent, desired_country: e.target.value})}
                    placeholder="e.g., Georgia, Uzbekistan"
                    data-testid="add-student-country-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="notes">Initial Notes</Label>
                <Textarea
                  id="notes"
                  value={newStudent.notes}
                  onChange={(e) => setNewStudent({...newStudent, notes: e.target.value})}
                  placeholder="Any initial notes about the student..."
                  rows={3}
                  data-testid="add-student-notes-input"
                />
              </div>
              
              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowAddDialog(false)}
                  data-testid="add-student-cancel-button"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  data-testid="add-student-submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Adding...
                    </>
                  ) : (
                    'Add Student'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Search & Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <Input
                  placeholder="Search by name, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="student-search-input"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter-select">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="hot">Hot</SelectItem>
                  <SelectItem value="warm">Warm</SelectItem>
                  <SelectItem value="cold">Cold</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={stageFilter} onValueChange={setStageFilter}>
                <SelectTrigger className="w-48" data-testid="stage-filter-select">
                  <SelectValue placeholder="All Stages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  <SelectItem value="inquiry">Inquiry</SelectItem>
                  <SelectItem value="application">Application</SelectItem>
                  <SelectItem value="document_submission">Document Submission</SelectItem>
                  <SelectItem value="offer_letter">Offer Letter</SelectItem>
                  <SelectItem value="visa">Visa</SelectItem>
                  <SelectItem value="departure">Departure</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Student List */}
      <div className="space-y-4">
        {filteredStudents.length > 0 ? (
          filteredStudents.map((student, index) => (
            <Card key={student.id} className={`hover-lift border-0 card-shadow slide-in-right`} style={{ animationDelay: `${index * 50}ms` }}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">
                        {student.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-semibold text-lg text-gray-900">{student.full_name}</h3>
                        <Badge variant="outline" className={getStatusBadgeClass(student.lead_status)}>
                          <i className="fas fa-circle text-xs mr-1"></i>
                          {formatStatusText(student.lead_status)}
                        </Badge>
                        <Badge variant="outline" className={getStageBadgeClass(student.admission_stage)}>
                          {formatStageText(student.admission_stage)}
                        </Badge>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span><i className="fas fa-envelope mr-1"></i>{student.email}</span>
                        <span><i className="fas fa-phone mr-1"></i>{student.phone}</span>
                        {student.desired_country && (
                          <span><i className="fas fa-globe mr-1"></i>{student.desired_country}</span>
                        )}
                        <span><i className="fas fa-calendar mr-1"></i>{new Date(student.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button asChild variant="outline" size="sm" className="hover:bg-indigo-50 hover:border-indigo-300">
                      <Link to={`/students/${student.id}`} data-testid={`view-student-${student.id}-button`}>
                        <i className="fas fa-eye mr-1"></i>
                        View Details
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-users text-gray-400 text-2xl"></i>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm || statusFilter || stageFilter ? 'No students found' : 'No students yet'}
              </h3>
              <p className="text-gray-500 mb-4">
                {searchTerm || statusFilter || stageFilter 
                  ? 'Try adjusting your search or filters'
                  : 'Start by adding your first student inquiry'
                }
              </p>
              {!searchTerm && !statusFilter && !stageFilter && (
                <Button 
                  onClick={() => setShowAddDialog(true)}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="add-first-student-button"
                >
                  <i className="fas fa-plus mr-2"></i>
                  Add Student
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default StudentList;
